import { useMemo } from "react";
import {
  formatIsoDateForLocale,
  parseIsoDate,
} from "@ascleo/types/date-time-validity";
import { useTranslation } from "react-i18next";
import { Building2, CalendarClock, Check, RefreshCw } from "lucide-react";
import { getInsuranceByWildcard, InsuranceAvatar } from "@ascleo/insurances-client";
import { useCheckInSend, useCheckInContext } from "../context";
import { useAudio } from "../../../../../lib/use-audio";
import { KioskStep } from "../../../../../components/kiosk-step";

function formatDate(dateString?: string) {
  if (!dateString) return null;
  try {
    return formatIsoDateForLocale(parseIsoDate(dateString.slice(0, 10)), "de-DE", {
      dateStyle: "long",
    });
  } catch {
    return null;
  }
}

/**
 * Insurer Pre-filled Step: a returning statutory patient answers one question,
 * "Ist das Ihre Krankenkasse?". The ways to obtain the proof (app, phone,
 * reception) follow on the next step, after the insurer is confirmed (KC-44).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <InsurerPreFilledStep />
 * ```
 */
export function InsurerPreFilledStep() {
  const { t } = useTranslation();
  const send = useCheckInSend();
  const context = useCheckInContext();

  // Play audio on mount
  useAudio("checkIn-insurerPreFilled");

  const handleConfirm = () => {
    if (context.previousInsurerIknr && context.previousInsurerName) {
      send({
        type: "INSURER_CONFIRMED",
        iknr: context.previousInsurerIknr,
        name: context.previousInsurerName,
      });
    }
  };

  const lastReadDate = formatDate(context.lastCardReadDate);

  const insuranceIdentity = useMemo(() => {
    if (!context.previousInsurerName) return null;
    return getInsuranceByWildcard(context.previousInsurerName)[0] ?? null;
  }, [context.previousInsurerName]);

  return (
    <KioskStep
      width="narrow"
      title={t("self-service.check-in.insurer-prefilled.question")}
      lead={t("self-service.check-in.insurer-prefilled.quarterly-notice-v2")}
      primaryAction={{
        label: t("self-service.check-in.insurer-prefilled.confirm-button"),
        icon: Check,
        onClick: handleConfirm,
        disabled: !context.previousInsurerIknr || !context.previousInsurerName,
        testId: "frontdesk-insurer-prefilled-confirm-btn",
      }}
      secondaryAction={{
        label: t("self-service.check-in.insurer-prefilled.change-button"),
        icon: RefreshCw,
        onClick: () => send({ type: "INSURER_CHANGED" }),
        testId: "frontdesk-insurer-prefilled-change-btn",
      }}
    >
      <section className="flex min-w-0 items-center gap-5 rounded-3xl border bg-card p-6">
        {insuranceIdentity ? (
          <InsuranceAvatar
            identity={insuranceIdentity}
            size="lg"
            showBackground={true}
            className="shrink-0 p-3"
          />
        ) : (
          <span
            aria-hidden
            className="grid size-16 shrink-0 place-items-center rounded-2xl bg-primary-subtle text-primary-ink [&_svg]:size-8"
          >
            <Building2 />
          </span>
        )}
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-base text-foreground">
            {t("self-service.check-in.insurer-prefilled.your-insurance")}
          </span>
          <span className="text-2xl font-semibold break-words">
            {context.previousInsurerName}
          </span>
        </div>
      </section>

      {lastReadDate ? (
        <p className="m-0 flex items-center gap-3 text-lg text-foreground">
          <CalendarClock aria-hidden className="size-6 shrink-0 text-muted-foreground" />
          {t("self-service.check-in.insurer-prefilled.last-read", {
            date: lastReadDate,
          })}
        </p>
      ) : null}
    </KioskStep>
  );
}
